import { Lesson, Objective, ObjectiveMedia, Project } from '@teachur/http-client';

/**
 * Aggregated statistics for a course, used by the instructor "Course Data" panel
 * to estimate contact/credit hours (e.g. for accreditation submissions).
 */
export interface CourseStats {
  numberOfObjectives: number;
  numberOfAssessments: number;
  numberOfMedia: number;
  /** Percentage (0-100) of objectives that have at least one assessment. */
  percentObjectivesWithAssessments: number;
  /** Percentage (0-100) of objectives that have at least one media item. */
  percentObjectivesWithMedia: number;
  /** Total media time in minutes (sum of ObjectiveMedia.time). */
  totalMediaTimeMinutes: number;
  /** Total exercise time in minutes (sum of lesson Project.time). */
  totalExerciseTimeMinutes: number;
  /** Total course time in minutes (media + exercise). */
  totalCourseTimeMinutes: number;
}

/** Sum a numeric field across a list, treating missing values as 0. */
export const sumByKey = <T>(items: T[] | undefined, key: keyof T): number =>
  (items ?? []).reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

/** Percentage of `count` out of `total`, rounded, guarding divide-by-zero. */
export const percentageOf = (count: number, total: number): number =>
  total > 0 ? Math.round((count * 100) / total) : 0;

/** Format a duration in minutes as `hh:mm` (zero-padded). */
export const formatMinutesToHHMM = (minutes: number): string => {
  const safeMinutes = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(mins)}`;
};

/**
 * Compute course statistics from the lessons and the fully-hydrated objectives
 * (each objective must include its `media` and `assessments` arrays).
 *
 * Note: in the current data model `Reading` has no `time` field, so reading
 * time is not included in the totals (unlike the legacy design).
 */
export const computeCourseStats = (
  lessons: Lesson[] | undefined,
  objectives: Objective[] | undefined
): CourseStats => {
  const safeObjectives = objectives ?? [];
  const safeLessons = lessons ?? [];

  const numberOfObjectives = safeObjectives.length;

  let numberOfAssessments = 0;
  let numberOfMedia = 0;
  let objectivesWithAssessments = 0;
  let objectivesWithMedia = 0;
  let totalMediaTimeMinutes = 0;

  safeObjectives.forEach((objective) => {
    const assessments = objective.assessments ?? [];
    const media: ObjectiveMedia[] = objective.media ?? [];

    if (assessments.length > 0) {
      numberOfAssessments += assessments.length;
      objectivesWithAssessments += 1;
    }

    if (media.length > 0) {
      numberOfMedia += media.length;
      objectivesWithMedia += 1;
      totalMediaTimeMinutes += sumByKey<ObjectiveMedia>(media, 'time');
    }
  });

  let totalExerciseTimeMinutes = 0;
  safeLessons.forEach((lesson) => {
    totalExerciseTimeMinutes += sumByKey<Project>(lesson.projects, 'time');
  });

  return {
    numberOfObjectives,
    numberOfAssessments,
    numberOfMedia,
    percentObjectivesWithAssessments: percentageOf(objectivesWithAssessments, numberOfObjectives),
    percentObjectivesWithMedia: percentageOf(objectivesWithMedia, numberOfObjectives),
    totalMediaTimeMinutes,
    totalExerciseTimeMinutes,
    totalCourseTimeMinutes: totalMediaTimeMinutes + totalExerciseTimeMinutes,
  };
};
